import { LibraryCategories, LibraryCategory, LibraryCategorySelectItem } from '@/store/modules/teamlibrary/types'
import { Filters } from '@/utils/filters'

export class CategoryHelpers {
  // eslint-disable-next-line lines-around-comment
  /**
   * Check if a category can be moved up in the sorted list
   * @param category The category to check
   * @param sortedCategories Sorted array of categories
   * @returns true if the category can be moved up
   */
  public static canMoveUp(category: LibraryCategory, sortedCategories: LibraryCategory[]): boolean {
    if (sortedCategories != null && sortedCategories.length > 0) {
      return sortedCategories[0].CategoryId !== category.CategoryId
    }
    return false
  }

  /**
   * Check if a category can be moved down in the sorted list
   * @param category The category to check
   * @param sortedCategories Sorted array of categories
   * @returns true if the category can be moved down
   */
  public static canMoveDown(category: LibraryCategory, sortedCategories: LibraryCategory[]): boolean {
    if (sortedCategories != null && sortedCategories.length > 0) {
      return sortedCategories[sortedCategories.length - 1].CategoryId !== category.CategoryId
    }
    return false
  }

  /**
   * Move a category up or down by swapping CategoryOrder with its neighbor
   * @param category The category to move
   * @param direction -1 for up, 1 for down
   * @param categories The categories array to modify (will be mutated)
   * @param unknownCategoryIndex The value representing an unknown/invalid index (typically -1)
   * @returns true if the move was successful
   */
  public static moveCategory(
    category: LibraryCategory,
    direction: number,
    categories: LibraryCategory[],
    unknownCategoryIndex: number = -1
  ): boolean {
    if (categories == null) {
      return false
    }

    // Get the categories in the same order as they're displayed in the table
    const clonedCategories = categories.slice()
    clonedCategories.sort(Filters.sortCategoriesByOrderAsc)
    const displayIndex = clonedCategories.findIndex((x: LibraryCategory) => x.CategoryId === category.CategoryId)
    const neighbourDisplayIndex = displayIndex + direction

    if (neighbourDisplayIndex < 0 || neighbourDisplayIndex >= clonedCategories.length) {
      return false
    }

    const neighbourCategory = clonedCategories[neighbourDisplayIndex]

    // Determine our data indices in the normal array
    const dataIndex = categories.indexOf(category)
    const neighbourDataIndex = categories.findIndex((x: LibraryCategory) => x.CategoryId === neighbourCategory.CategoryId)

    // Swap the category orders around
    if (dataIndex !== unknownCategoryIndex && neighbourDataIndex !== unknownCategoryIndex) {
      const existingCategoryOrder = categories[dataIndex].CategoryOrder
      categories[dataIndex].CategoryOrder = neighbourCategory.CategoryOrder
      categories[neighbourDataIndex].CategoryOrder = existingCategoryOrder

      // Handle edge case where orders are the same
      if (categories[dataIndex].CategoryOrder === existingCategoryOrder) {
        categories[dataIndex].CategoryOrder = neighbourCategory.CategoryOrder + direction
      }

      return true
    }

    return false
  }

  /**
   * Recalculate the CategoryOrder list for a LibraryCategories object
   * @param categories The categories array
   * @param hiddenStateId The StateId value representing hidden categories (typically 5)
   * @param skipFirstEntry Whether to skip the first entry (typically 'Any' with CategoryId 0)
   * @returns The new category order array
   */
  public static recalculateCategoryOrderList(
    categories: LibraryCategory[],
    hiddenStateId: number = 5,
    skipFirstEntry: boolean = true
  ): number[] {
    if (categories == null) {
      return []
    }

    // Exclude the first entry if specified (typically 'Any' CategoryID 0)
    const categoriesToProcess = skipFirstEntry ? categories.slice(1) : categories.slice()
    categoriesToProcess.sort(Filters.sortCategoriesByOrderAsc)

    const newOrder: number[] = []
    for (const c of categoriesToProcess) {
      if (c.StateId !== hiddenStateId) {
        newOrder.push(c.CategoryId)
      }
    }

    return newOrder
  }

  /**
   * Returns processed categories for select menus (filters hidden, marks deprecated, sorts by order)
   */
  public static buildCategorySelectItems(options: {
    tenantCategories?: LibraryCategories
    publicCategories?: LibraryCategories
    includePublic: boolean
    hiddenStateId?: number
    deprecatedStateId?: number
  }): LibraryCategorySelectItem[] {
    const {
      tenantCategories,
      publicCategories,
      includePublic,
      hiddenStateId = 5,
      deprecatedStateId = 999,
    } = options

    const tenantCategoriesItem = tenantCategories ? tenantCategories.Categories : null
    const publicCategoriesItem = publicCategories ? publicCategories.Categories : null
    const tenantList = this.processCategoriesForSelect(tenantCategoriesItem, hiddenStateId, deprecatedStateId)
    const publicList = includePublic
      ? this.processCategoriesForSelect(publicCategoriesItem, hiddenStateId, deprecatedStateId)
      : []

    const items: LibraryCategorySelectItem[] = []

    if (includePublic) {
      items.push({ header: 'Tenant categories' })
    }

    if (tenantList.length > 0) {
      items.push(...tenantList)
    }

    if (publicList.length > 0) {
      items.push({ divider: true })
      items.push({ header: 'Public categories' })
      items.push(...publicList)
    }

    return items.length > 0 ? items : tenantList
  }

  private static processCategoriesForSelect(
    categories?: LibraryCategory[] | null,
    hiddenStateId: number = 5,
    deprecatedStateId: number = 999
  ): LibraryCategory[] {
    if (!categories) {
      return []
    }

    const processed: LibraryCategory[] = []

    for (const c of categories) {
      if (c.StateId === hiddenStateId) {
        continue
      }
      const category = { ...c }
      if (category.StateId === deprecatedStateId) {
        category.DisplayName = `${category.DisplayName} (Deprecated)`
      }
      processed.push(category)
    }

    processed.sort(Filters.sortCategoriesByOrderAsc)
    return processed
  }
}
